/**
 * FAB Component - Constants
 * =========================
 * FAB icon-only. Colores de los tokens migrados (sin tokens nuevos).
 * soft usa opacity modifiers sobre canales RGB; primary usa color-primary hex.
 */

import { getIcon } from '../../icons';

export type FabIntent =
  | 'primary' | 'secondary' | 'danger' | 'success' | 'warning'
  | 'info' | 'caution' | 'prime' | 'prime-alt';
export type FabEmphasis = 'filled' | 'soft';
export type FabSize = 'sm' | 'md' | 'lg';
export type FabType = 'button' | 'submit' | 'reset';
export type FabIconType =
  | 'plus' | 'search' | 'check' | 'close' | 'star'
  | 'chat' | 'arrow-right' | 'trash' | 'download';

export interface FabSizeStyles {
  button: string;
  icon: string;
  label: string;
}

export interface FabDefaultValues {
  intent: FabIntent;
  emphasis: FabEmphasis;
  size: FabSize;
  type: FabType;
}

export const INTENT_EMPHASIS: Record<FabIntent, Record<FabEmphasis, string>> = {
  primary: {
    filled: 'ec-bg-color-primary ec-text-color-primary-contrast hover:ec-bg-color-primary-dark',
    soft: 'ec-bg-color-primary-light ec-text-color-primary-dark hover:ec-brightness-95'
  },
  secondary: {
    filled: 'ec-bg-gray-dark ec-text-white hover:ec-bg-black',
    soft: 'ec-bg-gray-extralight ec-text-gray-dark hover:ec-bg-gray-light'
  },
  danger: {
    filled: 'ec-bg-danger ec-text-white hover:ec-bg-danger/90',
    soft: 'ec-bg-danger/12 ec-text-danger hover:ec-bg-danger/20'
  },
  success: {
    filled: 'ec-bg-success ec-text-white hover:ec-bg-success/90',
    soft: 'ec-bg-success/12 ec-text-success hover:ec-bg-success/20'
  },
  warning: {
    filled: 'ec-bg-warning ec-text-white hover:ec-bg-warning/90',
    soft: 'ec-bg-warning/12 ec-text-warning hover:ec-bg-warning/20'
  },
  info: {
    filled: 'ec-bg-info ec-text-white hover:ec-bg-info/90',
    soft: 'ec-bg-info/12 ec-text-info hover:ec-bg-info/20'
  },
  caution: {
    filled: 'ec-bg-caution ec-text-white hover:ec-bg-caution/90',
    soft: 'ec-bg-caution/12 ec-text-caution hover:ec-bg-caution/20'
  },
  prime: {
    filled: 'ec-bg-prime ec-text-white hover:ec-bg-prime-dark',
    soft: 'ec-bg-prime/12 ec-text-prime hover:ec-bg-prime/20'
  },
  'prime-alt': {
    filled: 'ec-bg-prime-alt ec-text-white hover:ec-bg-prime-alt-dark',
    soft: 'ec-bg-prime-alt/12 ec-text-prime-alt hover:ec-bg-prime-alt/20'
  }
};

export const INTENT_RING: Record<FabIntent, string> = {
  primary: 'focus-visible:ec-ring-color-primary',
  secondary: 'focus-visible:ec-ring-gray',
  danger: 'focus-visible:ec-ring-danger',
  success: 'focus-visible:ec-ring-success',
  warning: 'focus-visible:ec-ring-warning',
  info: 'focus-visible:ec-ring-info',
  caution: 'focus-visible:ec-ring-caution',
  prime: 'focus-visible:ec-ring-prime',
  'prime-alt': 'focus-visible:ec-ring-prime-alt'
};

export const SIZES: Record<FabSize, FabSizeStyles> = {
  sm: { button: 'ec-w-10 ec-h-10 ec-rounded-2xl',  icon: 'ec-w-5 ec-h-5', label: 'ec-text-xs' },
  md: { button: 'ec-w-12 ec-h-12 ec-rounded-2xl', icon: 'ec-w-6 ec-h-6', label: 'ec-text-sm' },
  lg: { button: 'ec-w-14 ec-h-14 ec-rounded-2xl', icon: 'ec-w-7 ec-h-7', label: 'ec-text-sm' }
};

export const BASE_CLASSES = {
  wrapper: 'ec-inline-flex ec-flex-col ec-items-center ec-gap-2',
  button:
    'ec-inline-flex ec-items-center ec-justify-center ec-shadow-card ec-transition ec-cursor-pointer ' +
    'focus-visible:ec-outline-none focus-visible:ec-ring-2 focus-visible:ec-ring-offset-2',
  disabled: 'ec-bg-gray-light ec-text-gray ec-cursor-not-allowed ec-pointer-events-none ec-shadow-none',
  label: 'ec-text-color-text ec-font-medium'
};

/**
 * Iconos SVG predefinidos
 *
 * Las keys públicas (icon="close", etc.) se mantienen igual — cero breaking.
 * Los valores vienen del registro central (`src/icons`); `download` no tiene
 * equivalente en Figma y usa su SVG legacy vía `getIcon()`.
 */
export const ICONS: Record<FabIconType, string> = {
  plus: getIcon('plus') || '',
  search: getIcon('search') || '',
  check: getIcon('check') || '',
  close: getIcon('close') || '',
  star: getIcon('star') || '',
  chat: getIcon('chat') || '',
  'arrow-right': getIcon('arrow-right') || '',
  trash: getIcon('trash') || '',
  download: getIcon('download') || ''
};

export const SPINNER_SVG: string =
  `<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="3" stroke-opacity="0.25"/><path d="M21 12a9 9 0 00-9-9" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>`;

export const DEFAULTS: FabDefaultValues = {
  intent: 'primary',
  emphasis: 'filled',
  size: 'md',
  type: 'button'
};
